import api, { setAuthToken } from './api';

function useMockData() {
  return import.meta.env.VITE_USE_MOCK_DATA === 'true';
}

/**
 * Login with email and password
 */
export async function login(email, password, newPassword, session) {
  if (useMockData()) {
    // Mock login - accept any credentials
    await new Promise(resolve => setTimeout(resolve, 500));
    const mockToken = 'mock-token-12345';
    setAuthToken(mockToken);
    return {
      tokens: {
        accessToken: mockToken,
      },
      user: {
        userId: 'user-001',
        email,
        name: 'Mock User',
        role: 'captain',
      },
    };
  }

  const body = { email, password };
  if (newPassword && session) {
    body.newPassword = newPassword;
    body.session = session;
  }

  const response = await api.post('/auth/login', body);

  // Store all tokens if login successful
  // ID token for API Gateway Cognito authorizer, access token for verification, refresh token for renewals
  if (response.tokens?.idToken) {
    setAuthToken(response.tokens.idToken, response.tokens.accessToken, response.tokens.refreshToken);
    
    // Cache user data
    if (response.user) {
      localStorage.setItem('user', JSON.stringify(response.user));
    }
  }

  return response;
}

/**
 * Refresh authentication tokens using refresh token
 */
export async function refreshTokens() {
  if (useMockData()) {
    return { success: true };
  }

  try {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      console.error('No refresh token available');
      return { success: false, error: 'No refresh token' };
    }

    console.log('Calling /auth/refresh with refresh token...');
    const response = await api.post('/auth/refresh', { refreshToken });
    console.log('Refresh response:', response);

    if (response.tokens?.idToken) {
      // Update tokens but keep the same refresh token
      setAuthToken(response.tokens.idToken, response.tokens.accessToken, refreshToken);
      console.log('Tokens refreshed successfully');
      return { success: true };
    }

    console.error('No tokens in refresh response');
    return { success: false, error: 'No tokens in response' };
  } catch (error) {
    console.error('Token refresh failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Logout the current user
 */
export async function logout() {
  if (useMockData()) {
    setAuthToken(null, null, null);
    localStorage.removeItem('user');
    return;
  }

  try {
    await api.post('/auth/logout');
  } catch (err) {
    console.error('Logout API call failed:', err);
  } finally {
    // Clear tokens and user data even if logout fails
    setAuthToken(null, null, null);
    localStorage.removeItem('user');
  }
}

/**
 * Check if the current session is valid
 */
export async function checkAuth() {
  if (useMockData()) {
    // In mock mode, check if token exists to simulate persistence
    const token = localStorage.getItem('idToken') || localStorage.getItem('authToken');
    if (token) {
      return {
        valid: true,
        user: {
          userId: 'user-001',
          email: 'mock@example.com',
          name: 'Mock User',
          role: 'captain',
        }
      };
    }
    return { valid: false };
  }

  try {
    // Check if we have tokens
    const idToken = localStorage.getItem('idToken');
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    
    // No tokens at all - not logged in
    if (!idToken || !refreshToken) {
      setAuthToken(null, null, null);
      localStorage.removeItem('user');
      return { valid: false };
    }
    
    // If we have tokens but no access token, try to refresh
    if (!accessToken) {
      console.log('No access token found, attempting token refresh...');
      const refreshResult = await refreshTokens();
      
      if (!refreshResult.success) {
        setAuthToken(null, null, null);
        localStorage.removeItem('user');
        return { valid: false };
      }
    }
    
    // Try to get cached user data first
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        const user = JSON.parse(cachedUser);
        // Return cached user data, tokens are valid until API says otherwise
        return {
          valid: true,
          user,
        };
      } catch (e) {
        console.error('Failed to parse cached user data');
      }
    }
    
    // No cached user, verify with backend
    const newAccessToken = localStorage.getItem('accessToken');
    const response = await api.get('/auth/verify', {
      headers: {
        'Authorization': `Bearer ${newAccessToken}`
      }
    });

    // Cache user data
    if (response.user) {
      localStorage.setItem('user', JSON.stringify(response.user));
    }

    // Backend returns { user: {...} }, convert to expected format
    return {
      valid: true,
      user: response.user,
    };
  } catch (error) {
    console.error('Auth check failed:', error);
    
    // If auth check fails with 401, try refreshing tokens once
    if (error.status === 401) {
      console.log('Access token expired, attempting refresh...');
      const refreshResult = await refreshTokens();
      
      if (refreshResult.success) {
        // Retry auth check with new tokens
        try {
          const accessToken = localStorage.getItem('accessToken');
          const response = await api.get('/auth/verify', {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          });
          
          // Cache user data
          if (response.user) {
            localStorage.setItem('user', JSON.stringify(response.user));
          }
          
          return {
            valid: true,
            user: response.user,
          };
        } catch (retryError) {
          console.error('Auth check failed after refresh:', retryError);
        }
      }
    }
    
    // Clear tokens only after refresh attempt fails
    setAuthToken(null, null, null);
    localStorage.removeItem('user');
    return { valid: false };
  }
}
